import type { EntityDetailSchema } from '@web-config-app/core';
import type { LoadEntityRelationshipsSchema } from '../../types/operation.types';

export interface ResolveSchemaContext {
  schemaByType: Map<string, EntityDetailSchema>;
  schemaPromises: Map<string, Promise<EntityDetailSchema>>;
  loadEntityRelationshipsSchemas: LoadEntityRelationshipsSchema;
  schemas: Array<{ type: string; schema?: EntityDetailSchema; status: string }>;
  loadedTypes: Set<string>;
}

/**
 * Resolves a schema for a domain entity type, using cache when available or loading it if needed.
 * This function maintains a cache of loaded schemas and tracks in-flight requests
 * to avoid duplicating work for the same type.
 *
 * @param domainEntityType - The domain entity type to resolve schema for
 * @param context - Shared state for schema loading and caching
 * @returns Promise resolving to the schema
 * @throws {Error} If the schema cannot be loaded
 */
export async function resolveSchema(
  domainEntityType: string,
  context: ResolveSchemaContext,
): Promise<EntityDetailSchema> {
  const {
    schemaByType,
    schemaPromises,
    loadEntityRelationshipsSchemas,
    schemas,
    loadedTypes,
  } = context;

  // Return the cached schema when it's already available
  if (schemaByType.has(domainEntityType)) {
    return schemaByType.get(domainEntityType)!;
  }

  // Share any existing pending load for this type
  if (schemaPromises.has(domainEntityType)) {
    return schemaPromises.get(domainEntityType)!;
  }

  const loadPromise = (async () => {
    // Ask the caller to load relationship schemas for this specific type
    const newSchemas = await loadEntityRelationshipsSchemas([domainEntityType]);
    const trulyNewSchemas = newSchemas.filter(
      (schemaResult) => !loadedTypes.has(schemaResult.type),
    );

    if (trulyNewSchemas.length > 0) {
      schemas.push(...trulyNewSchemas);
      trulyNewSchemas.forEach((schemaResult) => {
        loadedTypes.add(schemaResult.type);
        if (schemaResult.schema && schemaResult.status === 'success') {
          schemaByType.set(schemaResult.type, schemaResult.schema);
        }
      });
    }

    const schema = schemaByType.get(domainEntityType);
    if (!schema) {
      throw new Error(
        `Unable to load schema for entity type: ${domainEntityType}`,
      );
    }
    return schema;
  })();

  schemaPromises.set(domainEntityType, loadPromise);

  try {
    return await loadPromise;
  } finally {
    schemaPromises.delete(domainEntityType);
  }
}
